/**
 * Centralized feature flags for the Virtual Profiler frontend.
 *
 * Introduced to hide features that are NOT part of the agreed CDTI technical
 * scope (Anexo 1a Resumen Ejecutivo / Anexo 1b Informe Técnico) from the demo.
 *
 * Each flag is the ENABLED state of a feature:
 *   - `false` hides the feature everywhere it is wired (Home nav, router guard,
 *     analysis panels, config forms).
 *   - `true` restores it.
 *
 * To re-enable a feature after the demo, flip its default below to `true`, or
 * set the matching `VUE_APP_FEATURE_*` env var to `'true'` in the build env —
 * no other code change is required. Wiring points are documented in
 * docs/alineacion-alcance-cdti.md.
 */

const getEnv = (key) => {
  if (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__[key] != null) {
    return window.__ENV__[key];
  }
  return typeof process !== 'undefined' && process.env ? process.env[key] : undefined;
};

const flag = (key, defaultEnabled) => {
  const raw = getEnv(key);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return defaultEnabled;
};

const featureFlags = {
  // Coherence engine: idea<->gesture coincidence panel, lie labeling, per-idea
  // score, and the coherence sub-section of the Interview Analysis Config form.
  coherence: flag('VUE_APP_FEATURE_COHERENCE', false),

  // "NEXT Semantica" = manual video analysis tool (upload + manual analysis
  // results). Covers the Home card and the /manual-video-analysis,
  // /coherence-evaluation and /coherence-analysis/:id routes.
  manualSemantica: flag('VUE_APP_FEATURE_MANUAL_SEMANTICA', false),

  // Share an analysis through a public token link (button + /shared/:token).
  shareReport: flag('VUE_APP_FEATURE_SHARE_REPORT', false),

  // Analysis control (versioned runs, queue and groups). Off by default until
  // the corresponding milestones ship; see
  // docs/plan-implementacion-versionado-y-control-analisis-2026-09-08.md.
  // Live queue: Home tile + /analysis-queue route (Waiting / Running / Recent).
  analysisQueue: flag('VUE_APP_FEATURE_ANALYSIS_QUEUE', false),

  // Analysis groups (named experiments over N videos): Home tile +
  // /analysis-groups route, group tabs in the manual video list and the
  // Global / Group selector in the coherence evaluation.
  analysisGroups: flag('VUE_APP_FEATURE_ANALYSIS_GROUPS', false),

  // Run history per video: run selector, version chips and Version details
  // panel in the analysis header (Promote / Pin / Compare / Replay).
  runHistory: flag('VUE_APP_FEATURE_RUN_HISTORY', false),

  // Folder Runs: a folder + a configuration + an engine version measured at a
  // point in time. Home tile + /runs and /runs/:id. The /engine-changelog page
  // is deliberately NOT gated: it is read-only reference material.
  folderRuns: flag('VUE_APP_FEATURE_FOLDER_RUNS', false),
};

export default featureFlags;
